import {
  FormControlLabel,
  Grid,
  Button,
  TextField,
  RadioGroup,
  Radio,
  Collapse,
  Chip,
  IconButton,
  ListItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import { useMetadataByKey } from "components/summary/hooks";
import React, { ChangeEvent, forwardRef, useImperativeHandle, useState, useEffect } from "react";
import styles from "./CheckboxFilter.module.scss";
import { Skeleton } from "@mui/material";

type BetaProgramFilterProps = IFilterParams & {
  onlyShowRadio?: boolean;
  showNumber?: boolean;
};

export interface CheckboxItem {
  checked: boolean;
  name: string;
  value: string;
}

const BetaProgramFilter = forwardRef<IFilterReactComp, BetaProgramFilterProps>(
  ({ filterChangedCallback, valueGetter, onlyShowRadio = false, showNumber = false }, ref) => {
    const [yesOrNo, setYesOrNo] = useState<"yes" | "no" | "">("");
    const [filterActive, setFilterActive] = useState(false);
    const [trigger, setTrigger] = useState(false);

    const { data: betaProgramList } = useMetadataByKey("beta-program");

    const [storeChecked, setStoreChecked] = useState<CheckboxItem[]>([]);
    const [keyword, setKeyWord] = useState("");

    useEffect(() => {
      if (betaProgramList) {
        const checkList = (betaProgramList as string[])?.map((beta: string) => {
          return { checked: false, name: beta, value: beta };
        });
        setStoreChecked(checkList);
      }
    }, [betaProgramList]);

    const checked = storeChecked?.filter((i) => i.checked);

    // expose AG Grid Filter Lifecycle callbacks
    useImperativeHandle(ref, () => {
      return {
        doesFilterPass(params) {
          const value = valueGetter(params.node);
          if (showNumber) {
            // for detail-contact part
            if (yesOrNo === "yes") {
              if (checked?.length) {
                // select yes and name(PS: nameA || nameB || (nameA & nameB) exist)
                return (
                  checked
                    ?.map((c) => {
                      return c.name;
                    })
                    ?.filter((m) => value.includes(m))?.length !== 0
                );
              } else {
                // only select yes not select name
                return value?.split("|")?.[0] > 0;
              }
            } else {
              return value === "0|";
            }
          } else {
            if (yesOrNo === "yes") {
              return value.includes("Yes");
            } else {
              return value === "No";
            }
          }
        },

        isFilterActive() {
          return Boolean(checked?.length > 0 || yesOrNo);
        },

        getModel() {
          if (!filterActive) return null;
          const savedStatus = {
            yesOrNo,
          };
          if (yesOrNo === "no") {
            return {
              type: "equals",
              filter: 0,
              savedStatus,
            };
          } else {
            if (onlyShowRadio) {
              return {
                type: "greaterThan",
                filter: 0,
                savedStatus,
              };
            } else {
              return {
                type: "in",
                filter: checked
                  ?.map((item) => {
                    return item.name;
                  })
                  .join("|"),
                savedStatus,
              };
            }
          }
        },

        setModel(model: any) {
          if (model?.savedStatus) {
            const { savedStatus, filter } = model;
            if (showNumber) {
              setYesOrNo("");
              setFilterActive(false);
              setTrigger(false);
            } else {
              setYesOrNo(savedStatus.yesOrNo ?? "");
              setFilterActive(true);
              setTrigger(true);
            }
            if (savedStatus.yesOrNo === "yes" && !onlyShowRadio) {
              setStoreChecked((prv) =>
                prv.map((i) => {
                  if (filter?.split("|")?.includes(i.name)) {
                    return { ...i, checked: true };
                  } else {
                    return { ...i, checked: false };
                  }
                })
              );
            }
          } else {
            setFilterActive(false);
            setTrigger(true);
            setYesOrNo("");
            setStoreChecked((store) =>
              store.map((i) => {
                return { ...i, checked: false };
              })
            );
          }
        },
      };
    });

    const onClear = () => {
      setYesOrNo("");
      setFilterActive(false);
      setStoreChecked((store) =>
        store.map((i) => {
          return { ...i, checked: false };
        })
      );
      setKeyWord("");
      setTrigger(true);
    };

    const onApply = () => {
      setFilterActive(true);
      setTrigger(true);
    };

    useEffect(() => {
      if (trigger) {
        filterChangedCallback();
        setTrigger(false);
      }
    }, [trigger, filterChangedCallback]);

    const handleRadioChange = (v: any) => {
      setYesOrNo(v);
      if (v === "no") {
        setKeyWord("");
        setStoreChecked((prv) =>
          prv.map((i) => {
            return { ...i, checked: false };
          })
        );
      }
    };

    const handleDelete = (item: CheckboxItem) => {
      setStoreChecked((prv) => prv.filter((i) => i.name !== item.name));
    };

    const onKeyWordChange = (e: ChangeEvent<HTMLInputElement>) => {
      setKeyWord(e.target.value);
    };

    const handleClearKeyword = () => {
      setKeyWord("");
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const tempData = storeChecked?.map((i) =>
        i.name === e.target.name ? { ...i, checked: e.target.checked } : i
      );
      setStoreChecked(tempData);
    };

    const handleLabelClick = (item: CheckboxItem) => () => {
      const tempData = storeChecked?.map((i) =>
        i.name === item.value ? { ...i, checked: !item.checked } : i
      );
      setStoreChecked(tempData);
    };

    return (
      <Grid
        container={true}
        direction="column"
        className="ag-filter-body-wrapper ag-simple-filter-body-wrapper"
      >
        <RadioGroup
          value={yesOrNo}
          //@ts-ignore
          onChange={(e, v) => handleRadioChange(v)}
          style={{ flexDirection: "column" }}
        >
          <FormControlLabel
            value="yes"
            control={<Radio color="primary" />}
            label="Yes"
          />
          <Collapse in={yesOrNo === "yes" && !onlyShowRadio}>
            <div className={styles.filterWrapper}>
              {/* chip area */}
              <div className={styles.chipWrapper}>
                {checked?.slice(0, 10).map((item) => (
                  <Chip
                    label={item.name}
                    onDelete={() => handleDelete(item)}
                    key={item.name}
                    size="small"
                    color="primary"
                    classes={{
                      root: styles.chipRoot,
                      label: styles.chipLabel,
                    }}
                  />
                ))}
                {checked?.length > 10 ? <div>{`${checked.length - 10} more`}</div> : null}
              </div>
              {/* search bar */}
              <TextField
                // fullWidth={true}
                placeholder="Enter Search Criteria..."
                margin="dense"
                autoFocus={true}
                value={keyword}
                onChange={onKeyWordChange}
                style={{ width: 240 }}
                InputProps={{
                  endAdornment: keyword ? (
                    <IconButton
                      onClick={handleClearKeyword}
                      size="small"
                    >
                      <HighlightOffOutlinedIcon
                        color="primary"
                        fontSize="small"
                      />
                    </IconButton>
                  ) : null,
                }}
              />
              {/*only scroll checkbox list */}
              <div style={{ textAlign: "center" }}>
                {betaProgramList !== undefined ? (
                  <div style={{ height: 200, overflowY: "scroll" }}>
                    {storeChecked
                      ?.filter((i) => i.name?.toLowerCase()?.includes(keyword?.toLowerCase()))
                      ?.map((item) => (
                        <ListItem
                          key={item.name}
                          style={{ padding: 0 }}
                        >
                          <ListItemIcon>
                            <Checkbox
                              checked={item.checked}
                              name={item.name}
                              onChange={handleChange}
                              color="primary"
                              classes={{
                                colorPrimary: styles.checkboxColorPrimary,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            onClick={handleLabelClick(item)}
                            style={{
                              cursor: "pointer",
                              width: 170,
                              overflowWrap: "break-word",
                            }}
                          />
                        </ListItem>
                      ))}
                  </div>
                ) : (
                  <>
                    {Array.from({ length: 4 }).map((x, i) => (
                      <Skeleton
                        key={`${x}${i}`}
                        width={160}
                        height={28}
                        variant="rectangular"
                        style={{ margin: "6px 12px" }}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          </Collapse>
          <FormControlLabel
            value="no"
            control={<Radio color="primary" />}
            label="No"
          />
        </RadioGroup>
        <Grid
          item={true}
          container={true}
          justifyContent="space-evenly"
        >
          <Grid item={true}>
            <Button
              variant="text"
              disabled={!filterActive}
              onClick={onClear}
              className="ag-standard-button ag-filter-apply-panel-button"
            >
              Reset filter
            </Button>
          </Grid>
          <Grid item={true}>
            <Button
              variant="text"
              className="ag-standard-button ag-filter-apply-panel-button"
              onClick={onApply}
              disabled={!Boolean(checked?.length || yesOrNo)}
            >
              Apply filter
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default BetaProgramFilter;
